import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';

export const automation_update: SelfContainedCommand = {
  id: 'automation_update',
  description: `Update an existing automation. Call automation_list first to get the automationId, then provide only the fields to change.

**UPDATABLE FIELDS:**
- name, description, systemPrompt, enabled
- triggers: full replacement of all triggers (world_event, cron, manual)
- schedule: convenience shorthand — cron expression to add/replace cron trigger (pass "" to remove)
- model: LLM model preference
- maxDurationSeconds: run timeout in seconds

**EXAMPLES:**
{ "automationId": "automation_abc123", "enabled": false }
{ "automationId": "automation_abc123", "schedule": "0 9 * * 1-5", "model": "claude-opus-4-5" }
{ "automationId": "automation_abc123", "triggers": [{ "type": "world_event", "eventType": "email.received" }] }`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',

  schema: {
    parameters: [
      { name: 'automationId', type: 'string', description: 'The ID of the automation to update', required: true },
      { name: 'name', type: 'string', description: 'New display name', required: false },
      { name: 'description', type: 'string', description: 'New description', required: false },
      { name: 'systemPrompt', type: 'string', description: 'New system prompt / standing instructions', required: false },
      { name: 'enabled', type: 'boolean', description: 'Enable or disable the automation', required: false },
      { name: 'triggers', type: 'array', description: "Full trigger replacement array. Each: { type: 'world_event'|'cron'|'manual', ... }", required: false },
      { name: 'schedule', type: 'string', description: 'Cron shorthand — adds/replaces cron trigger. Pass "" to remove. Ignored when triggers is provided.', required: false },
      { name: 'model', type: 'string', description: 'LLM model preference. Pass "" to reset to default.', required: false },
      { name: 'maxDurationSeconds', type: 'number', description: 'Run timeout in seconds', required: false },
    ],
  },

  examples: {
    good: [
      {
        input: { automationId: 'automation_abc123', enabled: false },
        expectedOutput: createSuccessResult('Updated automation', { automationId: 'automation_abc123', name: 'My Automation' }),
        description: 'Disable an automation',
      },
      {
        input: {
          automationId: 'automation_abc123',
          triggers: [{ type: 'world_event', eventType: 'email.received' }, { type: 'cron', cron: '0 8 * * 1' }],
        },
        expectedOutput: createSuccessResult('Updated automation', { automationId: 'automation_abc123', name: 'My Automation' }),
        description: 'Replace all triggers on an automation',
      },
    ],
    bad: [
      {
        input: { name: 'New Name' },
        error: 'automationId is required',
        why: 'Must identify which automation to update',
        fix: 'Use automation_list to find the automation ID first',
      },
    ],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      automationId,
      name,
      description,
      systemPrompt,
      enabled,
      triggers,
      schedule,

      model,
      maxDurationSeconds,
    } = params;

    if (!automationId) return createErrorResult('automationId is required', '', 'validation');

    const updates: Record<string, any> = {};

    if (name !== undefined) updates.name = name;
    if (description !== undefined) updates.description = description;
    if (systemPrompt !== undefined) updates.systemPrompt = systemPrompt;
    if (enabled !== undefined) updates.enabled = enabled;
    if (model !== undefined) updates.modelPreference = model === '' ? null : model;
    if (maxDurationSeconds !== undefined) updates.maxDurationSeconds = maxDurationSeconds;

    // triggers takes full precedence over schedule shorthand
    // The backend does not understand a bare "schedule" field — build triggers here
    if (triggers !== undefined) {
      updates.triggers = triggers;
    } else if (schedule !== undefined) {
      // Fetch existing triggers to preserve non-cron entries
      try {
        const existing = await fetchService(`${API_BASE_URL}/automations/${automationId}`, { method: 'GET' });
        const existingTriggers = (existing.skeleton?.triggers || []).filter((t: any) => t.type !== 'cron');
        if (schedule.trim()) {
          updates.triggers = [
            ...existingTriggers,
            { type: 'cron', cron: schedule.trim(), humanFrequency: schedule.trim() },
          ];
        } else {
          updates.triggers = existingTriggers.length > 0 ? existingTriggers : [{ type: 'manual' }];
        }
      } catch {
        // If fetch fails, pass schedule intent as triggers directly
        updates.triggers = schedule.trim()
          ? [{ type: 'cron', cron: schedule.trim(), humanFrequency: schedule.trim() }]
          : [{ type: 'manual' }];
      }
    }

    if (Object.keys(updates).length === 0) {
      return createErrorResult('No fields to update were provided', '', 'validation');
    }

    try {
      const updated = await fetchService(`${API_BASE_URL}/automations/${automationId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      return createSuccessResult(
        `Updated automation "${updated.name || name}"`,
        { automationId: updated._id || automationId, name: updated.name },
      );
    } catch (error: any) {
      return createErrorResult('Failed to update automation', error.message || 'Unknown error', 'execution');
    }
  },
};
